import React, { useCallback, useEffect, useMemo } from 'react';
import {
  CommandBarButton,
  Callout,
  ProgressIndicator,
  DelayedRender,
  Text,
} from 'office-ui-fabric-react';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import { useBoolean, useId } from '@uifabric/react-hooks';
import { downloadOrOpenBlob } from '../../utils/download';
import { getToken } from '../../utils/auth';

function useDownloadStatementMutation(url) {
  return useMutation(['downloadStatement', url], async () => {
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    if (!res.ok) {
      throw new Error(await res.text());
    }

    const blob = await res.blob();
    const fileName = res.headers.get('filename');
    await downloadOrOpenBlob(blob, fileName, false);
  });
}
export default function DownloadStatementButton({ statementUrl, payGoStatementUrl }) {
  const buttonId = useId('downloadStatement');
  const [isCalloutVisible, isCalloutVisibleCallbacks] = useBoolean(false);
  const downloadMutation = useDownloadStatementMutation(statementUrl);
  const downloadPaygoMutation = useDownloadStatementMutation(payGoStatementUrl);
  useEffect(() => {
    let timerId;
    if (downloadMutation.isLoading) {
      // if it's still loading after several seconds, then we want to show a loading
      // callout
      timerId = setTimeout(() => {
        if (downloadMutation.isLoading) {
          isCalloutVisibleCallbacks.setTrue();
        }
      }, 3000);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [downloadMutation, isCalloutVisibleCallbacks]);

  const downloadClicked = useCallback(async () => {
    try {
      await downloadMutation.mutateAsync();
      if (payGoStatementUrl) {
        await downloadPaygoMutation.mutateAsync();
      }
    } catch (error) {
      isCalloutVisibleCallbacks.setTrue();
    }
  }, [downloadMutation, downloadPaygoMutation]);

  const calloutText = useMemo(() => {
    if (downloadMutation.isLoading) {
      return 'Downloading statement, hang tight!';
    }

    if (downloadMutation.isError) {
      return 'Error downloading statement. Please try again.';
    }

    if (downloadMutation.isSuccess) {
      return 'Statement downloaded.';
    }
    return '';
  }, [downloadMutation]);

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        position: 'relative',
      }}
      role="menuitem"
    >
      <CommandBarButton
        id={buttonId}
        iconProps={{ iconName: 'ExcelDocument' }}
        onClick={downloadClicked}
        aria-label="Download Statement Button"
      >
        Download Statement
      </CommandBarButton>
      <ProgressIndicator
        styles={{
          root: {
            position: 'absolute',
            bottom: -4,
            left: 0,
            width: '100%',
            visibility: downloadMutation.isLoading ? 'visible' : 'hidden',
          },
        }}
      />

      {isCalloutVisible && (
        <Callout
          style={{ padding: '1rem' }}
          target={`#${buttonId}`}
          onDismiss={isCalloutVisibleCallbacks.setFalse}
          role="alert"
        >
          <DelayedRender>
            <Text variant="small">{calloutText}</Text>
          </DelayedRender>
        </Callout>
      )}
    </div>
  );
}

DownloadStatementButton.propTypes = {
  statementUrl: PropTypes.string.isRequired,
  payGoStatementUrl: PropTypes.string.isRequired,
};
